import React from 'react';

export interface QuotaMeterProps {
  label: string;
  used: number;
  /** null = illimité : pas de barre, juste le libellé. */
  limit: number | null;
  /** 'dark' pour la sidebar (fond slate-900), 'light' pour les pages. */
  tone?: 'light' | 'dark';
}

/** Barre de progression d'un quota. Seuils : < 80 % neutre, ≥ 80 % ambre, 100 % rouge.
 *  Le pourcentage est toujours lisible en texte — la couleur seule ne suffit pas. */
const QuotaMeter: React.FC<QuotaMeterProps> = ({ label, used, limit, tone = 'light' }) => {
  const isDark = tone === 'dark';
  const labelCls = isDark ? 'text-slate-400' : 'text-slate-500';
  const valueCls = isDark ? 'text-slate-300' : 'text-slate-600';
  const dimCls = isDark ? 'text-slate-500' : 'text-slate-400';

  if (limit === null) {
    return (
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs font-medium ${labelCls}`}>{label}</span>
        <span className={`text-xs tabular-nums ${valueCls}`}>
          {used} <span className={dimCls}>· Illimité</span>
        </span>
      </div>
    );
  }

  const pct = limit <= 0 ? 100 : Math.min(100, Math.round((used / limit) * 100));
  const barCls = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : isDark ? 'bg-slate-500' : 'bg-slate-400';
  const trackCls = isDark ? 'bg-slate-700/70' : 'bg-slate-100';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs font-medium ${labelCls}`}>{label}</span>
        <span className={`text-xs tabular-nums ${valueCls}`}>
          {used} / {limit} <span className={dimCls}>· {pct} %</span>
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.min(used, limit)}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuetext={`${used} sur ${limit} (${pct} %)`}
        className={`h-1.5 rounded-full overflow-hidden ${trackCls}`}
      >
        <div className={`h-full rounded-full transition-all ${barCls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default QuotaMeter;
